import Cookies from 'js-cookie';
import { apiRequest } from '@/utils/api-request';

export type ImportModule = 'students' | 'fees';

export interface ImportField {
  key: string;
  label: string;
  required: boolean;
  help?: string;
}

export interface ImportRowError {
  row_number: number;
  raw_row: Record<string, string>;
  error_message: string;
}

export interface UploadResult {
  headers: string[];
  rows: Record<string, string>[];
  preview: Record<string, string>[];
  totalRows: number;
}

export interface ValidateResult {
  totalRows: number;
  validCount: number;
  errorCount: number;
  errors: ImportRowError[];
}

export interface ImportRowWarning {
  row_number: number;
  message: string;
}

export interface CommitResult {
  batchId: number;
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: ImportRowError[];
  createdCount?: number;
  updatedCount?: number;
  paymentsCreatedCount?: number;
  paymentsSkippedCount?: number;
  warnings?: ImportRowWarning[];
}

const BASE = '/api/parent-app/admin/import';

const _apiBase = (): string =>
  (import.meta.env.VITE_API_URL as string) || 'http://localhost:8000/api';

export const bulkImportApi = {
  getTemplate: (module: ImportModule) =>
    apiRequest<{ fields: ImportField[] }>(`${BASE}/${module}/template`, { method: 'GET' }),

  uploadCsv: async (module: ImportModule, file: File) => {
    const token = Cookies.get('erp_auth_token') || Cookies.get('crm_auth_token');
    const formData = new FormData();
    formData.append('csvFile', file);
    try {
      const res = await fetch(`${_apiBase()}/parent-app/admin/import/${module}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token || ''}` },
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      return { success: data.success || res.ok, data: data.data as UploadResult | undefined, message: data.message as string | undefined };
    } catch (err: any) {
      return { success: false, message: err.message as string };
    }
  },

  validateRows: (module: ImportModule, rows: Record<string, string>[], columnMapping: Record<string, string>) =>
    apiRequest<ValidateResult>(`${BASE}/${module}/validate`, {
      method: 'POST',
      body: JSON.stringify({ rows, column_mapping: columnMapping }),
    }),

  commitRows: (module: ImportModule, rows: Record<string, string>[], columnMapping: Record<string, string>, fileName: string) =>
    apiRequest<CommitResult>(`${BASE}/${module}/commit`, {
      method: 'POST',
      body: JSON.stringify({ rows, column_mapping: columnMapping, file_name: fileName }),
    }),
};
